import { getWebInstrumentations, initializeFaro } from '@grafana/faro-react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import { onAuthChange } from '@/lib/auth-broadcast';
import { pushEvent } from './lib/faro';
import { routeTree } from './routeTree.gen';

export const getRouter = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
      },
    },
  });

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 30_000,
    defaultPendingMs: 150,
    defaultPendingMinMs: 200,
  });

  setupRouterSsrQueryIntegration({ router, queryClient });

  if (!router.isServer) {
    const faroCollectorUrl = import.meta.env.VITE_FARO_COLLECTOR_URL;

    if (faroCollectorUrl) {
      const coreApiUrl = import.meta.env.VITE_CORE_API_URL;
      initializeFaro({
        url: faroCollectorUrl,
        ignoreUrls: [faroCollectorUrl, /\/api\/o$/],
        app: {
          name: 'depot-web',
          version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0',
          environment: import.meta.env.VITE_ENVIRONMENT,
        },
        instrumentations: [
          ...getWebInstrumentations(),
          new TracingInstrumentation({
            instrumentationOptions: {
              propagateTraceHeaderCorsUrls: coreApiUrl
                ? [new RegExp(`^${coreApiUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`)]
                : [],
            },
          }),
        ],
        sessionTracking: {
          enabled: true,
          persistent: true,
        },
      });
    }

    router.subscribe('onResolved', ({ toLocation }) => {
      pushEvent('route-change', {
        path: toLocation.pathname,
        search: toLocation.searchStr || '',
      });
    });

    onAuthChange(queryClient);
  }

  return router;
};
